import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { toast } from 'react-toastify';
import { endOfDay } from 'date-fns';
import { isEmpty, capitalize } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  ButtonGroup,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import {
  Button,
  CardTitle,
  Field,
  PseudoStates,
  useFeatures,
  useSettings,
  useSession,
} from '@cordata/buffet';

import {
  authorizedPyInstance as Py,
} from '~/utils/api/instances';
import { deburrObject } from '~/utils/api/normalizers';

import useCit from '~/utils/cit/hook';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const Fieldset = ({ children, disabled }) => (
  <fieldset className="form-section" disabled={disabled}>
    {children}
  </fieldset>
);

Fieldset.propTypes = {
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
};

Fieldset.defaultProps = {
  disabled: false,
};

const PostCodeField = ({ names, states }) => {
  const { isDataLoaded, actions } = useCit();
  const {
    values, isSubmitting, setFieldValue, touched,
  } = useFormikContext();

  useEffect(() => {
    if (isDataLoaded
      && !isSubmitting
      && touched[names.postCode]
      && values[names.postCode]?.length === 5) {
      const postCode = values[names.postCode];
      const toastId = `address-autocomplete-${names.postCode}`;
      toast('autocompleting address', { toastId });
      toast.update(toastId, { render: 'autcompleting address' });
      actions.postalAutoComplete(postCode)
        .then((response) => {
          if (response.city) {
            setFieldValue(names.city, response.city);
            const province = states.find(state => (state.divisionLabel === response.state));
            setFieldValue(names.state, province);
            setFieldValue(names.county, response.county);
            toast.update(toastId, { type: toast.TYPE.SUCCESS, render: 'address completed' });
          } else {
            toast.update(toastId, { type: toast.TYPE.INFO, render: 'No Address Results Found' });
          }
        })
        .catch((except) => {
          toast.update(toastId, { type: toast.TYPE.ERROR, render: except.toString() });
        });
    }
  }, [values[names.postCode], touched[names.postCode]]);

  return (<Field
    name={names.postCode}
    label="Zip Code"
  />);
};

PostCodeField.propTypes = {
  states: PropTypes.arrayOf(PropTypes.shape({})),
  names: PropTypes.arrayOf(PropTypes.shape({
    city: PropTypes.string.isRequired,
    postCode: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
  })).isRequired,
};

PostCodeField.defaultProps = {
  states: [],
};

const LocationFields = ({ states, names, disabled }) => {
  return (
    <React.Fragment>
      <Row form>
        <Col sm={3}>
          <Field
            name={names.address1}
            label="Street Address"
          />
        </Col>

        <Col sm={3}>
          <Field
            name={names.address2}
            label="Suite/Apt/Unit"
          />
        </Col>

        <Col sm>
          <PostCodeField names={names} states={states} />
        </Col>

        <Col sm>
          <Field
            name={names.city}
            label="City"
          />
        </Col>

        <Col sm>
          <Field
            name={names.state}
            type="select"
            disabled={disabled}
            options={states}
            getOptionValue={o => o.divisionAbbrevIso}
            getOptionLabel={o => o.divisionAbbrevIso}
            label="State"
          />
        </Col>

        <Col sm>
          <Field
            name={names.county}
            label="County"
          />
        </Col>
      </Row>
    </React.Fragment>
  );
};

LocationFields.propTypes = {
  states: PropTypes.arrayOf(PropTypes.shape({})),
  names: PropTypes.shape().isRequired,
  disabled: PropTypes.bool,
};

LocationFields.defaultProps = {
  states: [],
  disabled: false,
};

const CIT = ({ formData, disabled }) => {
  const { isAuthed } = useSession();
  const { requiresSupervisoryApproval } = useSettings();
  const { hasSupervisoryApproval } = useFeatures();
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [t] = useTranslation();
  const location = useLocation();
  const { debugMode } = useSession();
  const {
    values, setStatus, status, setFieldValue,
  } = useFormikContext();
  const {
    formDataStatus,
    actions: { setIsDataLoaded },
  } = useCit();

  const [forceUsed, setForceUsed] = useState(false);
  const [injuryFromForce, setInjuryFromForce] = useState(false);

  useEffect(() => {
    setIsDataLoaded(status?.isFormLoaded);
  }, [status]);

  useEffect(() => {
    if (!isEmpty(values)) {
      setStatus({
        ...status,
        isFormLoaded: true,
      });
    }
  }, [values]);

  useEffect(() => {
    if (values.approvalStatus === 'rejected') {
      setIsRejectModalOpen(true);
    }
  }, [values.approvalStatus]);

  useEffect(() => {
    if (values.mHFollowUpRequested !== 'Yes') {
      setFieldValue('facilityUid', []);
    }
  }, [values.mHFollowUpRequested]);

  useEffect(() => {
    if (values.incidentHasRelatedCrime !== 'Yes') {
      setFieldValue('incidentHasRelatedCrimeYes', []);
      setFieldValue('criminalChargesToBeFiled', null);
    }
  }, [values.incidentHasRelatedCrime]);

  useEffect(() => {
    if (values.referralSourceUid
        && (values.referralSourceUid.referralSourceLabel || JSON.parse(values.referralSourceUid).referralSourceLabel).toLowerCase() !== 'other') {
      setFieldValue('otherReporting', null);
    }
  }, [values.referralSourceUid]);

  useEffect(() => {
    if (values.crisis?.toLowerCase() !== 'other') {
      setFieldValue('crisisOther', null);
    }
  }, [values.crisis]);

  useEffect(() => {
    const currentForceUsed = (values.response === 'Force Used');
    setForceUsed(currentForceUsed);
    if (!(currentForceUsed)) {
      setFieldValue('injuryResultedFromForce', null);
      setFieldValue('forceUsedIncidentResultedInInjury', []);
    }
  }, [values.response]);

  useEffect(() => {
    const currentInjury = values.forceUsedIncidentResultedInInjury &&
    forceUsed &&
      values.forceUsedIncidentResultedInInjury.length !== 0 &&
    !(
      values.forceUsedIncidentResultedInInjury.length === 1 &&
      values.forceUsedIncidentResultedInInjury[0].value === 'No Injury'
    );
    setInjuryFromForce(currentInjury);
    if (!currentInjury && status?.isFormLoaded) {
      setFieldValue('injuryResultedFromForce', null);
    }
  }, [values.forceUsedIncidentResultedInInjury, forceUsed]);

  useEffect(() => {
    if (values.subjectTakenIntoCustodyForEvaluation !== 'Yes') {
      setFieldValue('processInitiatedBy', null);
    }
  }, [values.subjectTakenIntoCustodyForEvaluation]);

  useEffect(() => {
    if (values.incidentHasRelatedCrime !== 'Yes') {
      setFieldValue('incidentHasRelatedCrimeYes', []);
      setFieldValue('criminalChargesToBeFiled', null);
    }
  }, [values.incidentHasRelatedCrime]);

  useEffect(() => {
    if (values.incidentHasRelatedCrimeYes?.length < 1) {
      setFieldValue('criminalChargesToBeFiled', null);
    }
  }, [values.incidentHasRelatedCrimeYes]);

  useEffect(() => {
    if (formDataStatus === 'SUCCESS' && typeof values.facilityUid === 'string') {
      Py
        .get(`api/data/public/facility/${values.facilityUid}`)
        .then(r => deburrObject(r.data.data))
        .then(r =>
          setFieldValue('facilityUid', [r]));
    }
  }, [formDataStatus, values.facilityUid]);

  useEffect(() => {
    if (formDataStatus === 'SUCCESS' && typeof values.shiftUid === 'string') {
      Py
        .get(`api/data/reference/reference-shift/${values.shiftUid}`)
        .then(r => deburrObject(r.data.data))
        .then(r =>
          setFieldValue('shiftUid', r));
    }
  }, [formDataStatus, values.shiftUid]);

  useEffect(() => {
    if (formDataStatus === 'SUCCESS' && typeof values.veteranStatus === 'string') {
      setFieldValue('veteranStatus', formData.veteranStatuses.find(o => o.value === values.veteranStatus));
    }
  }, [formDataStatus, values.veteranStatus]);

  if (formDataStatus !== 'SUCCESS') {
    return (<PseudoStates isLoading={formDataStatus === 'FETCHING'} error={formDataStatus === 'ERROR'} />);
  }

  return (
    <React.Fragment>
      <Fieldset disabled={disabled}>
        <CardTitle>{capitalize(t('patient'))} Information</CardTitle>

        <Row form>
          <Col sm>
            <Field
              name="firstName"
              required
            />
          </Col>

          <Col sm>
            <Field
              name="lastName"
              required
            />
          </Col>

          <Col sm={2}>
            <Field
              name="birthDate"
              type="date"
              format="MM/dd/yyyy"
              maxDate={endOfDay(new Date())}
            />
          </Col>

          <Col sm={1}>
            <Field
              name="genderUid"
              label="Sex"
              type="select"
              options={formData.genders}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.genderLabel}
              disabled={disabled}
              required
            />
          </Col>

          <Col sm={1}>
            <Field
              name="juvenile"
              type="checkbox"
              placeholder="Yes"
            />
          </Col>

          <Col sm>
            <Field
              name="veteranStatus"
              type="select"
              options={formData.veteranStatuses}
              required
            />
          </Col>
        </Row>

        <Row form>
          <Col sm={3}>
            <Field
              name="raceUid"
              label="Race"
              type="select"
              options={formData.races}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.raceLabel}
              disabled={disabled}
              required
              multi
            />
          </Col>

          <Col sm={3}>
            <Field
              name="phoneNumber"
              type="tel"
              required
            />
          </Col>

          <Col sm={6}>
            <Field
              name="phoneType"
              type="radio-group"
              inline
              options={formData.phoneTypeOptions}
              required
            />
          </Col>
        </Row>

        <LocationFields
          states={formData.states}
          names={{
            address1: 'addr1',
            address2: 'addr2',
            city: 'city',
            state: 'stateUid',
            postCode: 'postCode',
            county: 'county',
          }}
          disabled={disabled}
        />

        <Field
          name="priorityNote"
          type="textarea"
        />
      </Fieldset>

      <Fieldset disabled={disabled}>
        <CardTitle>Incident Details</CardTitle>

        <Row form>
          <Col sm={4}>
            <Field
              name="dateTime"
              type="date-time"
              required
              disableClock
              maxDate={endOfDay(new Date())}
            />
          </Col>

          <Col sm>
            <Field
              name="officer"
              required
            />
          </Col>

          <Col sm>
            <Field
              name="officerCit"
              label="CIT Officer"
              type="checkbox"
              placeholder="Yes"
            />
          </Col>

          <Col sm>
            <Field
              name="shiftUid"
              label="Shift"
              type="select"
              options={formData.shifts}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.shiftLabel}
              required
            />
          </Col>

          <Col sm>
            <Field
              name="totalTimeOnCall"
              label="Total Minutes"
              type="number"
            />
          </Col>
        </Row>

        <Row form>
          <Col sm>
            <Field
              name="type"
              label="Location Type"
              type="radio-group"
              inline
              options={formData.typeOptions}
            />
          </Col>
        </Row>

        <Row>
          <Col sm>
            <LocationFields
              states={formData.states}
              names={{
                address1: 'locationAddr1',
                address2: 'locationAddr2',
                city: 'locationCity',
                state: 'locationStateUid',
                postCode: 'locationPostCode',
                county: 'locationCounty',
              }}
              disabled={disabled}
            />
          </Col>
        </Row>

        {debugMode && location.pathname.includes('cit-details') && (
          <>
            <Row form>
              <Col sm>
                <Field
                  name="locationLatitude"
                  label="Latitude"
                  disabled
                />
              </Col>

              <Col sm>
                <Field
                  name="locationLongitude"
                  label="Longitude"
                  disabled
                />
              </Col>
            </Row>
          </>
        )}

        <Row form>
          <Col sm>
            <Field
              name="referralSourceUid"
              label="Person/Agency Reporting"
              type="radio-group"
              options={formData.referralSourceOptions}
              columns={3}
              getOptionValue={o => JSON.stringify(o)}
              getOptionLabel={o => o.referralSourceLabel}
              required
              clearable={false}
            />
          </Col>

          {values.referralSourceUid && (values.referralSourceUid.referralSourceLabel || JSON.parse(values.referralSourceUid).referralSourceLabel).toLowerCase() === 'other' && (
            <Col sm={4}>
              <Field
                name="otherReporting"
              />
            </Col>
          )}
        </Row>

        <Row form>
          <Col sm>
            <Field
              name="crisis"
              label="Select the most significant crisis or event related to this call for service."
              type="radio-group"
              columns={2}
              options={formData.crisisEventOptions}
              required
              clearable={false}
            />
          </Col>

          {values.crisis?.toLowerCase() === 'other' && (
            <Col sm={4}>
              <Field
                name="crisisOther"
                label="Other Crisis"
                type="textarea"
              />
            </Col>
          )}
        </Row>

        <Row form>
          <Col sm>
            <Field
              name="weaponInvolved"
              label="Weapon Involved"
              type="checkbox-group"
              options={formData.weaponInvolvedOptions}
              inline
            />
          </Col>
        </Row>
      </Fieldset>

      <Fieldset disabled={disabled}>
        <CardTitle>Intervention</CardTitle>

        <Row form>
          <Col sm>
            <Field
              name="response"
              type="radio-group"
              options={formData.responseOptions}
              clearable={false}
              required
            />
          </Col>

          <Col sm>
            <Field
              name="forceUsedIncidentResultedInInjury"
              label="Injured Parties"
              type="checkbox-group"
              columns={2}
              options={formData.forceUsedIncidentResultedInInjuryOptions}
              disabled={!(forceUsed)}
              required={forceUsed}
            />
          </Col>

          <Col sm>
            <Field
              name="injuryResultedFromForce"
              label="Injury Resulted From Force?"
              type="radio-group"
              options={formData.yesNo}
              disabled={
                !(injuryFromForce)
              }
              required={
                injuryFromForce
              }
              clearable={false}
            />
          </Col>
        </Row>

        <Row form>
          <Col sm>
            <Field
              name="dispositionUid"
              label="Disposition"
              type="select"
              disabled={disabled}
              options={formData.dispositions}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.statusLabel.split(' - ').pop()}
              required
            />
          </Col>

          <Col sm>
            <Field
              name="mHFollowUpRequested"
              label="Mental Health Follow-up Requested"
              type="radio-group"
              inline
              options={formData.yesNo}
              clearable={false}
            />
          </Col>

          <Col sm>
            <Field
              name="facilityUid"
              type="select"
              label="Facility"
              options={formData.facilities}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.facilityName}
              disabled={disabled || values.mHFollowUpRequested !== 'Yes'}
              required={values.mHFollowUpRequested === 'Yes'}
              multi
            />
          </Col>
        </Row>

        <Row form>
          <Col sm>
            <Field
              clearable={false}
              name="transportBy"
              type="radio-group"
              inline
              options={formData.transportByOptions}
            />
          </Col>

          <Col sm>
            <Field
              name="destination"
            />
          </Col>
        </Row>

        <Row form>
          <Col sm>
            <Field
              label="Client Taken Into Custody For Evaluation"
              name="subjectTakenIntoCustodyForEvaluation"
              type="radio-group"
              options={formData.yesNo}
              clearable={false}
              inline
            />
          </Col>

          <Col sm={8}>
            <Field
              name="processInitiatedBy"
              label="Evaluation Initiated By"
              type="radio-group"
              inline
              options={formData.processInitiatedByOptions}
              disabled={values.subjectTakenIntoCustodyForEvaluation !== 'Yes'}
              required={values.subjectTakenIntoCustodyForEvaluation === 'Yes'}
            />
          </Col>
        </Row>

        <Row form>
          <Col sm>
            <Field
              name="incidentHasRelatedCrime"
              label="Incident Has Related Crime?"
              type="radio-group"
              inline
              options={formData.yesNo}
              clearable={false}
            />
          </Col>

          <Col sm>
            <Field
              name="incidentHasRelatedCrimeYes"
              label="Crime Level"
              inline
              type="checkbox-group"
              options={formData.incidentHasRelatedCrimeOptions}
              disabled={values.incidentHasRelatedCrime !== 'Yes'}
              required={values.incidentHasRelatedCrime === 'Yes'}
            />
          </Col>

          <Col sm>
            <Field
              name="criminalChargesToBeFiled"
              label="Charges To Be Filed"
              type="radio-group"
              options={formData.criminalChargesToBeFiledOptions}
              disabled={values.incidentHasRelatedCrime !== 'Yes'}
              required={values.incidentHasRelatedCrime === 'Yes'}
              inline
              clearable={false}
            />
          </Col>
        </Row>

        <Field
          name="incidentNote"
          type="textarea"
        />
      </Fieldset>

      <Fieldset disabled={disabled}>
        <CardTitle>Filing Details</CardTitle>

        <Row form>
          <Col sm>
            <Field
              name="officerUnit"
            />
          </Col>

          <Col sm>
            <Field
              name="reportNumber"
            />
          </Col>

          <Col sm>
            <Field
              name="supervisor"
              label="Supervisor Name"
            />
          </Col>

          <Col sm>
            <Field
              name="supervisorUnit"
            />
          </Col>
        </Row>
      </Fieldset>

      { isAuthed && requiresSupervisoryApproval?.settingValue === 'true' && hasSupervisoryApproval && !location.pathname.includes('cit-details') && (
        <Fieldset>
          <CardTitle>Approval Status</CardTitle>

          <Row form>
            <ButtonGroup className="w-100">
              {[
                'rejected',
                'pending',
                'approved',
              ].map(b => (
                <Button
                  type="button"
                  color="tertiary"
                  outline={values.approvalStatus !== b}
                  onClick={() => setFieldValue('approvalStatus', b)}
                  disabled={values.approvalLocked || !hasSupervisoryApproval}
                >
                  {b}
                </Button>
              ))}
            </ButtonGroup>
          </Row>
        </Fieldset>
      )}
      {isRejectModalOpen &&
        <Modal
          isOpen={isRejectModalOpen}
          toggle={setIsRejectModalOpen}
        >
          <ModalHeader> <FontAwesomeIcon icon="exclamation-triangle" className="text-danger" size="lg" /> Reject CIT Submission </ModalHeader>
          <ModalBody>
            Rejecting a CIT contact sheet will permanently delete it from the system.
            There will be no way to make corrections or access it in the future.
            Do you want to proceed?
          </ModalBody>
          <ModalFooter>
            <Button outline type="cancel" onClick={() => setIsRejectModalOpen(false)}>Go Back</Button>
            <Button
              color="danger"
              type="button"
              outline
              onClick={() => setIsRejectModalOpen(false)}
            >Confirm
            </Button>
          </ModalFooter>
        </Modal>
}
    </React.Fragment>
  );
};

CIT.propTypes = {
  formData: PropTypes.shape().isRequired,
  disabled: PropTypes.bool,
};

CIT.defaultProps = {
  disabled: false,
};

export default CIT;
